import {
  Pagination,
  type PaginationProps,
  PaginationRange,
  cn,
} from '@sr/ui-library';
import type { ComponentProps } from 'react';

/** Compact pager used on MSR3 list pages (Tasks, Titles, Clients, Royalties ANZ). */
export type AppPaginationProps = Pick<
  PaginationProps,
  'className' | 'currentPage' | 'disabled' | 'onPageChange' | 'totalPages'
>;

export const AppPagination = ({
  className = 'mx-0 w-fit',
  ...props
}: AppPaginationProps) => (
  <Pagination
    boundaries={0}
    className={className}
    hasFirstButton={false}
    hasLastButton={false}
    siblings={0}
    {...props}
  />
);

type PaginationRangeProps = ComponentProps<typeof PaginationRange>;

export type AppPaginationFooterProps = Pick<
  PaginationProps,
  'currentPage' | 'disabled' | 'onPageChange' | 'totalPages'
> &
  Pick<PaginationRangeProps, 'pageSize' | 'totalItems'> & {
    /**
     * `totalItems` is a floor (`meta.totalItemsCapped`). Take it, `itemCount`
     * and `totalPages` from `cappedPagination(meta)`, so the pager can go past
     * the floor.
     */
    isTotalCapped?: boolean;
    /** Rows on the current page; read only when the total is capped. */
    itemCount?: number;
  };

type AppPaginationRangeProps = Pick<
  AppPaginationFooterProps,
  'currentPage' | 'isTotalCapped' | 'itemCount' | 'pageSize' | 'totalItems'
>;

/**
 * "26-50 of 5000+", in `PaginationRange`'s format. `PaginationRange` itself
 * clamps the page to the pages `totalItems` implies, which a capped total
 * understates.
 */
const CappedPaginationRange = ({
  currentPage,
  itemCount,
  pageSize,
  totalItems,
}: Omit<AppPaginationRangeProps, 'isTotalCapped'>) => {
  const rows = itemCount ?? pageSize;

  if (rows <= 0) return null;

  const first = (Math.max(currentPage, 1) - 1) * pageSize + 1;

  return (
    <span className="text-sm text-muted-foreground">
      {first}-{first + rows - 1} of {totalItems}+
    </span>
  );
};

/**
 * The item counter, capped or not. For a counter laid out apart from its
 * pager; {@link AppPaginationSummary} pairs the two.
 */
export const AppPaginationRange = ({
  isTotalCapped,
  ...props
}: AppPaginationRangeProps) =>
  isTotalCapped ? (
    <CappedPaginationRange {...props} />
  ) : (
    <PaginationRange
      page={props.currentPage}
      pageSize={props.pageSize}
      totalItems={props.totalItems}
    />
  );

/**
 * The item counter beside the pager, shared by {@link AppPaginationFooter} and
 * {@link ListToolbar}; only the layout around it differs.
 */
export const AppPaginationSummary = ({
  className,
  currentPage,
  disabled,
  isTotalCapped,
  itemCount,
  onPageChange,
  pageSize,
  totalItems,
  totalPages,
}: AppPaginationFooterProps & { className?: string }) => (
  <div className={cn('flex flex-wrap items-center gap-3', className)}>
    <AppPaginationRange
      currentPage={currentPage}
      isTotalCapped={isTotalCapped}
      itemCount={itemCount}
      pageSize={pageSize}
      totalItems={totalItems}
    />

    <AppPagination
      currentPage={currentPage}
      disabled={disabled}
      totalPages={totalPages}
      onPageChange={onPageChange}
    />
  </div>
);

/**
 * Top + bottom footer matching {@link TasksTabPage} layout. Renders nothing
 * when everything fits on one page, so callers need no guard of their own.
 */
export const AppPaginationFooter = ({
  totalPages,
  ...props
}: AppPaginationFooterProps) => {
  if (totalPages <= 1) return null;

  return (
    <AppPaginationSummary
      className="w-full justify-between"
      totalPages={totalPages}
      {...props}
    />
  );
};
